'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  fullName: string;
  email: string;
  company: string;
  phone: string;
  monthlyLeads: string;
  currentChallenges: string[];
  preferredDate: string;
  preferredTime: string;
}

const ConsultationModal = ({ isOpen, onClose }: ConsultationModalProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    monthlyLeads: '',
    currentChallenges: [],
    preferredDate: '',
    preferredTime: ''
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const challenges = [
    'Low lead quality',
    'Poor conversion rates',
    'Manual processes',
    'Lack of personalization',
    'Limited analytics',
    'High marketing costs'
  ];

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleChallengeToggle = (challenge: string) => {
    setFormData(prev => ({
      ...prev,
      currentChallenges: prev.currentChallenges.includes(challenge)
        ? prev.currentChallenges.filter(c => c !== challenge)
        : [...prev.currentChallenges, challenge]
    }));
  };

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    try {
      // Send form data to API endpoint
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formType: 'marketing-consultation',
          ...formData
        }),
      });

      const result = await response.json();

      if (result.success) {
        console.log('Form submitted successfully:', formData);
        onClose();
        setStep(1);
        setFormData({
          fullName: '',
          email: '',
          company: '',
          phone: '',
          monthlyLeads: '',
          currentChallenges: [],
          preferredDate: '',
          preferredTime: ''
        });
      } else {
        console.error('Error submitting form:', result.error);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  if (!isOpen || !isHydrated) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-bold text-foreground">Schedule Your Consultation</h2>
            <p className="text-sm text-muted-foreground mt-1">Step {step} of 3</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      s <= step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {s}
                  </div>
                  {s < 3 && (
                    <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-primary' : 'bg-muted'}`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-heading font-bold text-foreground mb-4">Contact Information</h3>
              
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  placeholder="Your Company Inc."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-heading font-bold text-foreground mb-4">Tell Us About Your Needs</h3>
              
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">Monthly Lead Volume</label>
                <select
                  value={formData.monthlyLeads}
                  onChange={(e) => handleInputChange('monthlyLeads', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                >
                  <option value="">Select range</option>
                  <option value="0-100">0-100 leads/month</option>
                  <option value="100-500">100-500 leads/month</option>
                  <option value="500-1000">500-1,000 leads/month</option>
                  <option value="1000-5000">1,000-5,000 leads/month</option>
                  <option value="5000+">5,000+ leads/month</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-3">Current Challenges (Select all that apply)</label>
                <div className="grid grid-cols-2 gap-3">
                  {challenges.map((challenge) => (
                    <button
                      key={challenge}
                      onClick={() => handleChallengeToggle(challenge)}
                      className={`px-4 py-3 rounded-lg border-2 text-sm font-semibold transition-all duration-300 ${
                        formData.currentChallenges.includes(challenge)
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'bg-background border-border text-foreground hover:border-primary'
                      }`}
                    >
                      {challenge}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-heading font-bold text-foreground mb-4">Schedule Your Call</h3>
              
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">Preferred Date</label>
                <input
                  type="date"
                  value={formData.preferredDate}
                  onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">Preferred Time</label>
                <select
                  value={formData.preferredTime}
                  onChange={(e) => handleInputChange('preferredTime', e.target.value)}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                >
                  <option value="">Select time</option>
                  <option value="9:00 AM">9:00 AM</option>
                  <option value="10:00 AM">10:00 AM</option>
                  <option value="11:00 AM">11:00 AM</option>
                  <option value="1:00 PM">1:00 PM</option>
                  <option value="2:00 PM">2:00 PM</option>
                  <option value="3:00 PM">3:00 PM</option>
                  <option value="4:00 PM">4:00 PM</option>
                </select>
              </div>

              <div className="bg-muted rounded-xl p-6 mt-6">
                <h4 className="text-sm font-heading font-bold text-foreground mb-4 uppercase tracking-wide">
                  What to Expect
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                    <p className="text-sm text-muted-foreground">30-minute strategy session with an AI marketing expert</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                    <p className="text-sm text-muted-foreground">Custom ROI analysis for your specific situation</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                    <p className="text-sm text-muted-foreground">Actionable recommendations you can implement immediately</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            {step > 1 && (
              <button
                onClick={handleBack}
                className="px-6 py-3 bg-muted text-foreground font-heading font-semibold rounded-lg hover:bg-muted/80 transition-all duration-300 flex items-center space-x-2"
              >
                <Icon name="ArrowLeftIcon" size={20} />
                <span>Back</span>
              </button>
            )}
            
            {step < 3 ? (
              <button
                onClick={handleNext}
                className="ml-auto px-6 py-3 bg-primary text-primary-foreground font-heading font-semibold rounded-lg hover:bg-primary/90 transition-all duration-300 flex items-center space-x-2"
              >
                <span>Next</span>
                <Icon name="ArrowRightIcon" size={20} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="ml-auto px-6 py-3 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 transition-all duration-300 flex items-center space-x-2"
              >
                <Icon name="CheckIcon" size={20} />
                <span>Schedule Consultation</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationModal;